<template>
    <settings-wrapper title="Organization settings" section="General">
        <v-row>
            <v-col cols="12">
                <h3 class="secondary--text">Space Features</h3>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-alert type="info" text dense class="mb-0">Features enabled here will be available for all spaces created in this organization.</v-alert>
            </v-col>
            <v-col v-for="(feature, index) in features" :key="index" cols="12" sm="6" md="4">
                <org-feature-status :feature="feature" class="h-100" />
            </v-col>
        </v-row>

        <v-row v-if="isOrgManager" class="mt-6">
            <v-col cols="12">
                <h3 class="secondary--text">Space Lifecycle</h3>
            </v-col>
        </v-row>
        <v-row v-if="isOrgManager" class="mb-6">
            <v-col cols="12" md="4">
                <v-card>
                    <v-card-title>Automatic resting</v-card-title>
                    <v-form :key="currentOrgData && currentOrgData.oid" ref="restingForm" v-model="restingFormValid" @submit.prevent="saveRestingSettings">
                        <v-card-text class="pt-0 pb-0">
                            <v-row dense>
                                <v-col cols="12">
                                    <v-switch
                                        v-model="restingEnabled"
                                        label="Enable automatic resting"
                                        :disabled="!restingSettingsReady || savingRestingSettings || !restingPolicySupported"
                                        hide-details
                                        class="mt-0 pt-0"></v-switch>
                                </v-col>
                                <v-col v-if="restingEnabled" cols="12" class="mt-3">
                                    <v-text-field
                                        v-model="restingThresholdDays"
                                        v-mask="integerMask"
                                        label="Days since last application start"
                                        inputmode="numeric"
                                        outlined
                                        dense
                                        :rules="[positiveWholeDays]"
                                        :disabled="!restingSettingsReady || savingRestingSettings || !restingPolicySupported"
                                        required></v-text-field>
                                </v-col>
                                <v-col v-if="restingSettingsReady && !restingPolicySupported" cols="12">
                                    <v-alert type="warning" text dense class="mb-0">
                                        This organization uses an unsupported resting policy. Contact support to change it.
                                    </v-alert>
                                </v-col>
                            </v-row>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                type="submit"
                                text
                                color="primary"
                                :loading="savingRestingSettings"
                                :disabled="
                                    !restingSettingsReady ||
                                    !restingPolicySupported ||
                                    !restingSettingsChanged ||
                                    (restingEnabled && !restingFormValid) ||
                                    savingRestingSettings
                                ">
                                Save
                            </v-btn>
                        </v-card-actions>
                    </v-form>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <h3 class="error--text">Danger Zone</h3>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Leave organization</v-card-title>
                    <v-card-actions>
                        <EntityLeaveDialog entity-type="organization" />
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </settings-wrapper>
</template>

<script>
import eventBus from '@/components/EventBus'
import OrgFeatureStatus from '@/components/OrgFeatureStatus.vue'
import { mapGetters, mapState } from 'vuex'
import SettingsWrapper from '@/components/SettingsWrapper.vue'
import EntityLeaveDialog from '@/components/EntityLeaveDialog.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as organizationErrors from '@/modules/home/errors/organizations'
import * as permissionsErrors from '@/utils/errors/permissions'

const { getBackendErrorMessage } = createBackendErrorHandler(organizationErrors, permissionsErrors)

const integerMask = createNumberMask({
    prefix: '',
    includeThousandsSeparator: false,
    allowDecimal: false,
    allowNegative: false
})

const featureProperties = {
    loading: false,
    enabled: false,
    canEnable: false,
    disabledReason: '',
    error: '',
    fn: () => {}
}

export default {
    name: 'OrganizationConfig',
    components: { OrgFeatureStatus, EntityLeaveDialog, SettingsWrapper },
    computed: {
        ...mapState(['currentOrgData']),
        ...mapGetters('orgStore', ['isOrgManager']),
        restingSettingsReady() {
            return this.currentOrgData && String(this.currentOrgData.oid) === String(this.$route.params.oid)
        },
        restingPolicySupported() {
            const trigger = this.currentOrgData?.org_configs?.resting_trigger
            return !trigger || trigger === 'app_start_activity'
        },
        restingSettingsChanged() {
            const configs = this.currentOrgData?.org_configs || {}
            const enabled = configs.resting_trigger === 'app_start_activity'
            return this.restingEnabled !== enabled || (this.restingEnabled && Number(this.restingThresholdDays) !== Number(configs.resting_threshold_days))
        }
    },
    data() {
        return {
            integerMask,
            restingEnabled: false,
            restingThresholdDays: null,
            restingFormValid: false,
            savingRestingSettings: false,
            switchingFeature: '',
            features: {
                vimeo: {
                    ...featureProperties,
                    label: 'Video library',
                    description: 'Upload videos to make your content more interactive on Nuvolos.'
                },
                hpc: {
                    ...featureProperties,
                    label: 'Credit-based sizes',
                    description: 'Scale up your application resources using credits.'
                },
                tables: {
                    ...featureProperties,
                    label: 'Database tables',
                    description: 'Use the Scientific Data Warehouse to work with massive amounts of tabular data.'
                }
            }
        }
    },
    created() {
        eventBus.$on('global-dialog-accept', this.enableFeature)
        Object.keys(this.features).forEach(this.fetchStatusOfFeature)
    },
    beforeDestroy() {
        eventBus.$off('global-dialog-accept')
    },
    methods: {
        positiveWholeDays(value) {
            return (Number.isSafeInteger(Number(value)) && Number(value) > 0) || 'Enter a positive whole number of days'
        },
        async saveRestingSettings() {
            if (!this.isOrgManager || !this.restingSettingsReady || !this.restingPolicySupported || !this.restingSettingsChanged || this.savingRestingSettings)
                return
            if (this.restingEnabled && !this.$refs.restingForm.validate()) return

            const { oid } = this.$route.params
            const enabled = this.restingEnabled
            const thresholdDays = Number(this.restingThresholdDays)
            let settingsSaved = false
            this.savingRestingSettings = true
            try {
                const configs = { ...(this.currentOrgData.org_configs || {}) }
                if (configs.resting_trigger && configs.resting_trigger !== 'app_start_activity') {
                    throw new Error('Unsupported resting policy')
                }
                if (enabled) {
                    configs.resting_trigger = 'app_start_activity'
                    configs.resting_threshold_days = thresholdDays
                } else {
                    delete configs.resting_trigger
                }
                const { data } = await this.$axios.patch(`/orgs/${oid}`, { org_configs: configs })
                settingsSaved = true
                if (String(this.currentOrgData?.oid) === String(oid)) {
                    this.$store.commit('setCurrentOrgData', data)
                }
                const { data: orgs } = await this.$axios.post('/orgs/find', {})
                this.$store.commit('setUserOrgs', orgs)
                const refreshedOrg = orgs.find(org => String(org.oid) === String(oid))
                if (refreshedOrg && String(this.currentOrgData?.oid) === String(oid)) {
                    this.$store.commit('setCurrentOrgData', refreshedOrg)
                }
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Automatic resting settings saved.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                const snackBarText = getBackendErrorMessage(
                    error,
                    settingsSaved ? 'Settings saved, but failed to refresh organization data.' : 'Failed to save automatic resting settings.'
                )
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            } finally {
                this.savingRestingSettings = false
            }
        },
        setSwitchDemoEnabled(enabled) {
            return false
        },
        confirmFeatureSwitch(feature) {
            return async newState => {
                this.switchingFeature = feature
                const dialogTitle = `${newState ? 'Enabling' : 'Disabling'} ${this.features[feature].label}`
                const dialogText =
                    feature === 'switch'
                        ? newState
                            ? `Once Switch Cloud integration is enabled, Space Administrators will be able to enable Switch Cloud resources in their spaces individually.`
                            : `By turning off Switch Cloud integration, Space Administrators won't be able to enable Switch Cloud resources in additional spaces. Spaces that already have it enabled will remain enabled.`
                        : newState
                        ? `Once this feature is enabled, Space Administrators will be able to enable it in their spaces individually.`
                        : `By turning off this feature, Space Administrators won't be able to change their space's feature flag anymore. This means, spaces which already have this feature enabled will remain enabled, but the feature cannot be enabled anymore for other spaces.`
                this.$store.dispatch('showGlobalDialog', {
                    dialogTitle,
                    dialogText,
                    dialogAction: ['cancel', 'accept']
                })
            }
        },
        async enableFeature() {
            const feature = this.switchingFeature
            if (!feature) return
            const { oid } = this.$route.params
            const f = this.features[feature]
            f.loading = true

            if (feature === 'switch') {
                this.setSwitchDemoEnabled(!f.enabled)
                f.enabled = this.getSwitchDemoEnabled()
                f.loading = false
                return
            }

            try {
                const { data } = await this.$axios.patch(`orgs/${oid}`, {
                    [`${feature}_enabled`]: !f.enabled
                })
                f.loading = false
                this.$store.commit('setCurrentOrgData', data)
            } catch (error) {
                console.error(error)
                const snackBarText = getBackendErrorMessage(
                    error,
                    `Failed to ${this.features[feature].enabled ? 'disable' : 'enable'} ${this.features[feature].label.toLowerCase()}, please try again later!`
                )
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
                f.loading = false
            }
        },
        fetchStatusOfFeature(feature) {
            const f = this.features[feature]
            f.loading = true
            f.fn = this.confirmFeatureSwitch(feature)

            if (feature === 'switch') {
                f.enabled = this.getSwitchDemoEnabled()
                f.canEnable = true
                f.disabledReason = ''
                f.error = ''
                f.loading = false
                return
            }

            this.$axios
                .get(`/orgs/${this.$route.params.oid}/can_enable_${feature}`)
                .then(({ data }) => {
                    if (this.currentOrgData) f.enabled = this.currentOrgData[`${feature}_enabled`]
                    f.canEnable = data[`can_enable_${feature}`]
                    f.disabledReason = data.reason
                    f.loading = false
                })
                .catch(error => {
                    f.loading = false
                    f.error = error.message
                })
        }
    },
    watch: {
        currentOrgData: {
            immediate: true,
            handler(org) {
                if (!org) return
                for (const feature in this.features) {
                    if (feature === 'switch') continue
                    this.features[feature].enabled = org[`${feature}_enabled`]
                }
                this.restingEnabled = org.org_configs?.resting_trigger === 'app_start_activity'
                this.restingThresholdDays = org.org_configs?.resting_threshold_days ?? null
            }
        }
    }
}
</script>
